import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { LoginSchema, type LoginDto } from '@valiosascrm/shared';
import api from '@/lib/api';
import { Button, Input, FormField, Checkbox } from '@/v2/design-system/primitives';
import { PasswordField, Rise } from '@/v2/components/auth';
import { finishCookieLogin } from '@/v2/utils/auth-flow';
import { sortClinics } from '@/lib/tenant-context';
import { landingFor } from '@/v2/utils/landing';
import { useSingleFlightCallback } from '@/hooks/useSingleFlightCallback';
import { shelvedClinicFor, takeRecoveredOrigin } from '@/stores/workDrafts';
import { requestVersionCheck } from '@/lib/app-version';

/**
 * Login defaults to the first available clinic; subsequent switches live in the topbar.
 * Story 65.45: quando a sessão caiu com trabalho digitado, a mesma pessoa volta para a clínica
 * e para a tela onde estava, sem recarregar a página.
 */
export default function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  // Onda 0 (UX-06): o erro do login mora embaixo da senha e some assim que a pessoa
  // corrige um campo ou tenta de novo — nunca fica preso na tela.
  const [loginError, setLoginError] = useState<string | null>(null);
  const clearLoginError = () => setLoginError(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginDto>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const completeLogin = async (expectedTenantId?: string) => {
    // 45.5 (CG-3): landing por role — professional cai na agenda; demais roles em /dashboard.
    const user = await finishCookieLogin(queryClient, expectedTenantId);
    // Story 65.45 (exceção ao guarda da RC-063): o trabalho que a queda da sessão pôs de lado
    // só existe na memória desta aba. Se ele acabou de voltar para a mesma pessoa, a aba segue
    // sem recarregar e o vigia de versão confere a publicação agora: havendo versão nova, ele
    // recarrega na primeira troca de tela depois de o trabalho ser salvo ou descartado.
    const origin = takeRecoveredOrigin();
    if (origin) {
      requestVersionCheck();
      navigate(origin, { replace: true });
      return;
    }
    // RC-063: nos outros casos o login recarrega a página inteira — código e dados da clínica
    // saem do servidor agora, nunca de uma aba aberta antes do último deploy.
    window.location.assign(landingFor(user?.role));
  };

  const onSubmit = useSingleFlightCallback(async (data: LoginDto) => {
    setLoginError(null);
    setIsLoading(true);
    try {
      const res = await api.post('/auth/login', data);
      let expectedTenantId: string | undefined;
      if (res.data.requiresTenantSelection) {
        const clinics = sortClinics<{ id: string; name: string }>(res.data.tenants ?? []);
        // 65.45: havendo trabalho guardado desta pessoa, volta para a clínica onde a sessão caiu
        // (se ela ainda puder entrar lá); senão, a primeira pelo nome, como sempre.
        const shelved = shelvedClinicFor(data.email);
        const chosen = clinics.find((clinic) => clinic.id === shelved) ?? clinics[0];
        if (!chosen) throw new Error('Nenhuma clínica disponível para este acesso.');
        expectedTenantId = chosen.id;
        await api.post('/auth/login', { ...data, tenantId: chosen.id });
      }
      if (res.data.requiresOnboarding) {
        navigate('/onboarding-empresa', {
          state: { email: data.email, invitations: res.data.invitations ?? [] },
        });
        return;
      }
      await completeLogin(expectedTenantId);
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string };
      const message = e.response?.data?.message || e.message || 'Erro ao fazer login';
      setLoginError(message === 'Credenciais inválidas' ? 'E-mail ou senha não conferem. Use "Esqueci a senha".' : message);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <>
      <Rise index={0}>
        <h1 className="text-so-28 font-bold text-sober-text">Bem-vinda de volta</h1>
        <p className="mt-1 text-so-14 text-sober-secondary">
          Entre para continuar gerenciando sua clínica.
        </p>
      </Rise>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 flex flex-col gap-4">
        <Rise index={1}>
          <FormField label="E-mail" htmlFor="login-email" error={errors.email?.message}>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              placeholder="voce@clinica.com.br"
              {...register('email', { onChange: clearLoginError })}
            />
          </FormField>
        </Rise>
        <Rise index={2}>
          <FormField label="Senha" htmlFor="login-password" error={errors.password?.message ?? loginError ?? undefined}>
            <PasswordField
              id="login-password"
              autoComplete="current-password"
              placeholder="••••••••"
              {...register('password', { onChange: clearLoginError })}
            />
          </FormField>
        </Rise>
        <Rise index={3} className="flex items-center justify-between">
          <label className="flex min-h-11 cursor-pointer items-center gap-2 text-so-14 text-sober-text">
            <Checkbox
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
              aria-label="Lembrar de mim"
            />
            Lembrar de mim
          </label>
          <Link
            to="/forgot-password"
            className="inline-flex min-h-11 items-center text-so-14 font-bold text-sober-primary hover:underline"
          >
            Esqueci a senha
          </Link>
        </Rise>
        <Rise index={4}>
          <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
            {isLoading ? 'Entrando…' : 'Entrar'}
          </Button>
        </Rise>
      </form>

      <Rise index={5}>
        <p className="mt-6 text-center text-so-14 text-sober-secondary">
          Não tem uma conta?{' '}
          <Link to="/register" className="inline-flex min-h-11 items-center font-bold text-sober-primary hover:underline">
            Criar conta grátis
          </Link>
        </p>
      </Rise>


    </>
  );
}
